import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Search, ChevronDown, X } from 'lucide-react';
import { TradingMode } from '../App';
import { useUnifiedPrices } from '../contexts/UnifiedPriceContext';

import { TOP_CRYPTO_PAIRS, CFD_INSTRUMENTS } from '../constants/tradingPairs';
import { getListedCfdInstrument, matchesCfdSearch } from '../../supabase/functions/_shared/cfdInstruments';
import ListedCfdLogo from './ListedCfdLogo';
import { getListedCfdLogo } from '../constants/listedCfdLogos';

interface SearchablePairSelectorProps {
  selectedPair: string;
  onPairSelect: (pair: string) => void;
  tradingMode: 'spot' | 'futures' | 'cfd' | 'binary' | 'robot' | 'wallet' | 'events';
  marketDataList?: any[]; // Not used anymore as we're using predefined pairs
}

const SearchablePairSelector: React.FC<SearchablePairSelectorProps> = ({
  selectedPair,
  onPairSelect,
  tradingMode,
}) => {
  const { getPriceBySymbol } = useUnifiedPrices();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);


  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Get filtered tickers based on trading mode and search
  const getFilteredTickers = useCallback(() => {
    if (tradingMode === 'cfd') {
      let cfdInstruments = [...CFD_INSTRUMENTS];

      cfdInstruments = cfdInstruments.filter(ticker => {
        const price = getPriceBySymbol(ticker.symbol);
        return price > 0 || !!getListedCfdInstrument(ticker.symbol);
      });

      if (searchTerm.trim()) {
        const term = searchTerm.toLowerCase();
        cfdInstruments = cfdInstruments.filter(ticker =>
          matchesCfdSearch(ticker, term)
        );
      }

      return cfdInstruments;
    } else {
      let cryptoPairs = [...TOP_CRYPTO_PAIRS];

      cryptoPairs = cryptoPairs.filter(ticker => {
        const price = getPriceBySymbol(ticker.symbol);
        return price > 0;
      });

      if (searchTerm.trim()) {
        const term = searchTerm.toLowerCase();
        cryptoPairs = cryptoPairs.filter(ticker =>
          ticker.symbol.toLowerCase().includes(term) ||
          ticker.name.toLowerCase().includes(term)
        );
      }

      return cryptoPairs;
    }
  }, [tradingMode, searchTerm, getPriceBySymbol]);

  const handlePairSelect = (symbol: string) => {
    onPairSelect(symbol);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  // Format pair display based on trading mode
  const formatPairDisplay = (symbol: string, name?: string, type?: string) => {
    // For stocks and indices, show the name
    if (type === 'stock' || type === 'index' || type === 'etf') {
      return name || symbol;
    }
    // For crypto pairs, format as BTC/USDT
    if (symbol.endsWith('USDT')) {
      return symbol.replace('USDT', '/USDT');
    }
    // For CFD instruments, return the symbol as-is (already formatted correctly)
    return symbol;
  };

  const getCategoryIcon = (type: string) => {
    switch (type) {
      case 'forex': return '💱';
      case 'commodity': return '🥇';
      case 'stock': return '📈';
      case 'crypto': return '₿';
      case 'index': return '📊';
      default: return '📊';
    }
  };

  const filteredTickers = getFilteredTickers();

  const renderPairIcon = (symbol: string, type: string) => getListedCfdLogo(symbol)
    ? <ListedCfdLogo symbol={symbol} />
    : <span className="text-lg">{getCategoryIcon(type)}</span>;

  // Get current pair display name
  const getCurrentPairInfo = () => {
    if (tradingMode === 'cfd') {
      const instrument = CFD_INSTRUMENTS.find(i => i.symbol === selectedPair);
      return instrument;
    }
    const cryptoPair = TOP_CRYPTO_PAIRS.find(p => p.symbol === selectedPair);
    return cryptoPair;
  };

  const currentPairInfo = getCurrentPairInfo();
  const currentPairDisplay = currentPairInfo
    ? formatPairDisplay(selectedPair, currentPairInfo.name, currentPairInfo.type)
    : selectedPair;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 bg-slate-700 hover:bg-slate-600 px-3 py-2 rounded-lg transition-colors"
      >
        <ListedCfdLogo symbol={selectedPair} className="w-8 h-8" />
        <h1 className="text-2xl font-bold text-white">
          {currentPairDisplay}
          {tradingMode === 'futures' && <span className="text-sm text-slate-400 ml-2">Perp</span>}
          {tradingMode === 'cfd' && <span className="text-sm text-slate-400 ml-2">CFD</span>}
        </h1>
        <ChevronDown size={16} className="text-slate-400" />
      </button>

      {isOpen && (
        <div className="fixed mt-2 bg-white border border-slate-200 rounded-xl shadow-xl w-96 max-h-96 overflow-hidden" style={{ zIndex: 100, left: '80px', top: '60px' }}>
          {/* Search Header */}
          <div className="p-4 border-b border-slate-200">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
              <input
                ref={inputRef}
                type="text"
                placeholder={`Search ${tradingMode === 'cfd' ? 'instruments' : 'pairs'}...`}
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full bg-slate-50 text-slate-900 pl-10 pr-10 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              {searchTerm && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-900"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>

          {/* Results */}
          <div className="max-h-80 overflow-y-auto scrollbar-hide">
            {filteredTickers.length > 0 ? (
              <div className="p-2">
                {/* Group by type for CFD mode */}
                {tradingMode === 'cfd' && (
                  <>
                    {/* Forex Section */}
                    <div className="text-xs font-semibold text-slate-600 uppercase mt-2 mb-1 px-3">
                      Forex Pairs
                    </div>
                    {filteredTickers
                      .filter(ticker => ticker.type === 'forex')
                      .map((ticker) => (
                        <button
                          key={ticker.symbol}
                          onClick={() => handlePairSelect(ticker.symbol)}
                          className={`w-full text-left px-3 py-2 rounded hover:bg-slate-100 transition-colors flex items-center justify-between ${
                            selectedPair === ticker.symbol ? 'bg-blue-50 text-blue-600' : 'text-slate-900'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            {renderPairIcon(ticker.symbol, ticker.type)}
                            <div>
                              <div className="font-medium">{formatPairDisplay(ticker.symbol, ticker.name, ticker.type)}</div>
                              <div className="text-xs text-slate-500 truncate max-w-48">{ticker.name}</div>
                            </div>
                          </div>
                          <span className="text-xs text-slate-400 uppercase">{ticker.type}</span>
                        </button>
                      ))}
                    
                    {/* Commodities Section */}
                    <div className="text-xs font-semibold text-slate-600 uppercase mt-4 mb-1 px-3">
                      Commodities
                    </div>
                    {filteredTickers
                      .filter(ticker => ticker.type === 'commodity')
                      .map((ticker) => (
                        <button
                          key={ticker.symbol}
                          onClick={() => handlePairSelect(ticker.symbol)}
                          className={`w-full text-left px-3 py-2 rounded hover:bg-slate-100 transition-colors flex items-center justify-between ${
                            selectedPair === ticker.symbol ? 'bg-blue-50 text-blue-600' : 'text-slate-900'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            {renderPairIcon(ticker.symbol, ticker.type)}
                            <div>
                              <div className="font-medium">{formatPairDisplay(ticker.symbol, ticker.name, ticker.type)}</div>
                              <div className="text-xs text-slate-500 truncate max-w-48">{ticker.name}</div>
                            </div>
                          </div>
                          <span className="text-xs text-slate-400 uppercase">{ticker.type}</span>
                        </button>
                      ))}
                    
                    {/* Stocks Section */}
                    {filteredTickers.some(t => t.type === 'stock') && (
                      <>
                        <div className="text-xs font-semibold text-slate-600 uppercase mt-4 mb-1 px-3">
                          Stocks
                        </div>
                        {filteredTickers
                          .filter(ticker => ticker.type === 'stock')
                          .map((ticker) => (
                            <button
                              key={ticker.symbol}
                              onClick={() => handlePairSelect(ticker.symbol)}
                              className={`w-full text-left px-3 py-2 rounded hover:bg-slate-100 transition-colors flex items-center justify-between ${
                                selectedPair === ticker.symbol ? 'bg-blue-50 text-blue-600' : 'text-slate-900'
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                {renderPairIcon(ticker.symbol, ticker.type)}
                                <div>
                                  <div className="font-medium">{formatPairDisplay(ticker.symbol, ticker.name, ticker.type)}</div>
                                  <div className="text-xs text-slate-500 truncate max-w-48">{ticker.symbol}</div>
                                </div>
                              </div>
                              <span className="text-xs text-slate-400 uppercase">{ticker.type}</span>
                            </button>
                          ))}
                      </>
                    )}

                    {/* Indices & ETFs Section */}
                    {filteredTickers.some(t => t.type === 'index' || t.type === 'etf') && (
                      <>
                        <div className="text-xs font-semibold text-slate-600 uppercase mt-4 mb-1 px-3">
                          Indices & ETFs
                        </div>
                        {filteredTickers
                          .filter(ticker => ticker.type === 'index' || ticker.type === 'etf')
                          .map((ticker) => (
                            <button
                              key={ticker.symbol}
                              onClick={() => handlePairSelect(ticker.symbol)}
                              className={`w-full text-left px-3 py-2 rounded hover:bg-slate-100 transition-colors flex items-center justify-between ${
                                selectedPair === ticker.symbol ? 'bg-blue-50 text-blue-600' : 'text-slate-900'
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                {renderPairIcon(ticker.symbol, ticker.type)}
                                <div>
                                  <div className="font-medium">{formatPairDisplay(ticker.symbol, ticker.name, ticker.type)}</div>
                                  <div className="text-xs text-slate-500 truncate max-w-48">{ticker.symbol}</div>
                                </div>
                              </div>
                              <span className="text-xs text-slate-400 uppercase">{ticker.type}</span>
                            </button>
                          ))}
                      </>
                    )}
                  </>
                )}
                
                {/* Non-CFD mode - just list all pairs */}
                {tradingMode !== 'cfd' && filteredTickers.map((ticker) => (
                  <button
                    key={ticker.symbol}
                    onClick={() => handlePairSelect(ticker.symbol)}
                    className={`w-full text-left px-3 py-2 rounded hover:bg-slate-100 transition-colors flex items-center justify-between ${
                      selectedPair === ticker.symbol ? 'bg-blue-50 text-blue-600' : 'text-slate-900'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {renderPairIcon(ticker.symbol, ticker.type)}
                      <div>
                        <div className="font-medium">{formatPairDisplay(ticker.symbol, ticker.name, ticker.type)}</div>
                        <div className="text-xs text-slate-500 truncate max-w-48">{ticker.name}</div>
                      </div>
                    </div>
                    <span className="text-xs text-slate-400 uppercase">{ticker.type}</span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="p-4 text-center text-slate-500">
                {searchTerm ? `No ${tradingMode === 'cfd' ? 'instruments' : 'pairs'} found` : `No ${tradingMode === 'cfd' ? 'instruments' : 'pairs'} available`}
              </div>
            )}
          </div>

          {/* Footer info */}
          <div className="p-3 border-t border-slate-200 bg-slate-50">
            <div className="text-xs text-slate-600 text-center">
              {filteredTickers.length} {tradingMode === 'cfd' ? 'instruments' : 'pairs'} available
              {searchTerm && ` • Filtered by "${searchTerm}"`}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchablePairSelector;
